import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  PointElement,
  LineElement,
} from 'chart.js'

import { getChartPalette } from '@digital-twins/deck-ui/foundations/theme/chart-palettes'

let _registered = false

/**
 * Ensure Chart.js is registered exactly once (client-only usage).
 * Color defaults are re-applied every call so theme switches take effect.
 */
export function ensureChartJsRegistered() {
  if (!_registered) {
    ChartJS.register(
      ArcElement,
      Tooltip,
      Legend,
      CategoryScale,
      LinearScale,
      RadialLinearScale,
      BarElement,
      PointElement,
      LineElement,
      chartJsThemeTextPlugin
    )
    ChartJS.defaults.font.family = 'Inter, system-ui, -apple-system, sans-serif'
    ChartJS.defaults.animation = false
    ChartJS.defaults.responsive = true
    ChartJS.defaults.maintainAspectRatio = false
    _registered = true
  }

  const textColor = resolveCanvasColor('var(--slide-text-primary)', 'var(--ds-text-primary)')
  ChartJS.defaults.color = textColor
  ChartJS.defaults.plugins.legend.labels.color = textColor
  ChartJS.defaults.plugins.tooltip.bodyColor = textColor
  ChartJS.defaults.plugins.tooltip.titleColor = textColor
}

/**
 * Resolve a CSS variable color (e.g., 'var(--slide-chart-1)') into a concrete color string
 * so it can be used reliably in canvas rendering.
 */
export function resolveCanvasColor(color: string, fallback = 'var(--ds-text-primary)'): string {
  const resolve = (input: string, backup: string, depth: number): string => {
    if (depth > 6) return backup
    if (typeof input !== 'string') return resolve(backup, backup, depth + 1)

    const trimmed = input.trim()
    if (!trimmed) return resolve(backup, backup, depth + 1)

    const m = trimmed.match(/var\(\s*(--[^),\s]+)\s*(?:,\s*([^)]+))?\)/)
    if (!m) return wrapBareHsl(trimmed)

    const varName = m[1]
    const inlineFallback = m[2]?.trim()
    if (typeof window !== 'undefined') {
      const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
      if (value) return wrapBareHsl(value)
    }

    if (inlineFallback) {
      return resolve(inlineFallback, backup, depth + 1)
    }

    if (trimmed === backup) return backup
    return resolve(backup, backup, depth + 1)
  }

  return resolve(color, fallback, 0)
}

/** Tailwind CSS variables use bare HSL (e.g. "0 0% 100%") which Canvas can't parse. */
function wrapBareHsl(value: string): string {
  if (/^[\d.]+\s+[\d.]+%\s+[\d.]+%$/.test(value)) return `hsl(${value})`
  return value
}

type Rgb = { r: number; g: number; b: number }

/**
 * Convert a canvas color to an rgba() string with the requested alpha, when possible.
 *
 * This is used to soften filled shapes (bars/arcs) so charts feel less visually heavy
 * in a slide deck context.
 */
export function withCanvasAlpha(color: string, alpha01: number): string {
  const a = Math.max(0, Math.min(1, alpha01))
  const c = (color || '').trim()
  if (!c) return c

  // rgb(...) -> rgba(...)
  if (c.startsWith('rgb(')) return c.replace(/^rgb\(/, 'rgba(').replace(/\)\s*$/, `, ${a})`)

  // rgba(...) -> replace trailing alpha
  if (c.startsWith('rgba(')) return c.replace(/,\s*([0-9]*\.?[0-9]+)\s*\)\s*$/, `, ${a})`)

  // #rgb / #rrggbb -> rgba(...)
  const rgb = parseCanvasColor(c)
  if (rgb) return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`

  // Unknown format; best-effort: return as-is.
  return c
}

function parseCanvasColor(color: string): Rgb | null {
  const c = (color || '').trim()
  if (!c) return null

  // #rgb / #rrggbb
  if (c.startsWith('#')) {
    const hex = c.slice(1)
    const full = hex.length === 3 ? hex.split('').map((x) => x + x).join('') : hex
    if (full.length !== 6) return null
    const n = Number.parseInt(full, 16)
    if (!Number.isFinite(n)) return null
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
  }

  // rgb(...) / rgba(...)
  const rgbMatch = c.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  if (rgbMatch) {
    return { r: Number(rgbMatch[1]), g: Number(rgbMatch[2]), b: Number(rgbMatch[3]) }
  }

  const hslMatch = c.match(/hsla?\s*\(\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)%\s*[,\s]\s*(\d+(?:\.\d+)?)%/i)
  if (hslMatch) {
    return hslToRgb(Number(hslMatch[1]), Number(hslMatch[2]), Number(hslMatch[3]))
  }

  const bareHslMatch = c.match(/^(-?\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/)
  if (bareHslMatch) {
    return hslToRgb(Number(bareHslMatch[1]), Number(bareHslMatch[2]), Number(bareHslMatch[3]))
  }

  return null
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const hue = ((h % 360) + 360) % 360
  const sat = Math.max(0, Math.min(100, s)) / 100
  const light = Math.max(0, Math.min(100, l)) / 100
  const c = (1 - Math.abs(2 * light - 1)) * sat
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1))
  const m = light - c / 2
  let r1 = 0
  let g1 = 0
  let b1 = 0
  if (hue < 60) {
    r1 = c
    g1 = x
  } else if (hue < 120) {
    r1 = x
    g1 = c
  } else if (hue < 180) {
    g1 = c
    b1 = x
  } else if (hue < 240) {
    g1 = x
    b1 = c
  } else if (hue < 300) {
    r1 = x
    b1 = c
  } else {
    r1 = c
    b1 = x
  }
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  }
}

function lerpColor(a: Rgb, b: Rgb, t: number): Rgb {
  const tt = Math.max(0, Math.min(1, t))
  return {
    r: Math.round(a.r + (b.r - a.r) * tt),
    g: Math.round(a.g + (b.g - a.g) * tt),
    b: Math.round(a.b + (b.b - a.b) * tt),
  }
}

function rgbToString(c: Rgb): string {
  return `rgb(${c.r}, ${c.g}, ${c.b})`
}

export function getDeckChartPalette(): string[] {
  const vars = [
    'var(--slide-chart-1)',
    'var(--slide-chart-2)',
    'var(--slide-chart-3)',
    'var(--slide-chart-4)',
    'var(--slide-chart-5)',
    'var(--slide-chart-6)',
    'var(--slide-chart-7)',
    'var(--slide-chart-8)',
  ]
  return vars.map((v) => resolveCanvasColor(v, v))
}

export function pickDeckChartPalette(count: number): string[] {
  const palette = getDeckChartPalette()
  // Slot 8 is the neutral overflow grey: it is only ever the 8th colour, never a spread pick.
  if (count >= palette.length) {
    return palette.slice(0, count)
  }
  return pickChartPalette(palette.slice(0, 7), count)
}

/**
 * Deck's default (forest) series palette: primary first, then the remaining
 * stops, each palette colour once. Reads the static palette data
 * directly rather than `--slide-chart-*` CSS vars, which are only populated by
 * BrandProvider for whiteLabel/brandedTheme accounts and otherwise undefined
 * on report-page routes (tokens.css is only loaded inside deck-render
 * contexts like SlideFrame).
 */
export function getAppChartPalette(): string[] {
  const palette = getChartPalette(null)
  const primary = palette.colors[palette.primaryIndex] ?? palette.colors[0]
  const supporting = palette.colors.filter((_, index) => index !== palette.primaryIndex)
  return [primary, ...supporting].map((tuple) => `hsl(${tuple})`)
}

export function pickAppChartPalette(count: number): string[] {
  return pickChartPalette(getAppChartPalette(), count)
}

/**
 * Hue-separated series colors for multi-trace charts (grouped bars, radar).
 * Do not use pickDeckChartPalette / pickAppChartPalette here — those are a
 * single-hue lightness ramp.
 */
export const CATEGORICAL_CHART_COLORS = [
  'hsl(var(--chart-primary))',
  'hsl(var(--chart-accent))',
  'hsl(var(--chart-tertiary))',
  'hsl(var(--chart-quinary))',
  'hsl(var(--chart-senary))',
  'hsl(var(--chart-secondary))',
] as const

export function getCategoricalChartPalette(): string[] {
  return [...CATEGORICAL_CHART_COLORS]
}

export function pickCategoricalChartPalette(count: number): string[] {
  const palette = getCategoricalChartPalette()
  const n = Math.max(0, Math.floor(count))
  if (n <= 0 || palette.length === 0) return []
  return Array.from({ length: n }, (_, i) => palette[i % palette.length])
}

/**
 * Select a spread of colors across a palette (e.g., 3 picks from 6).
 */
export function pickChartPalette(palette: readonly string[], count: number): string[] {
  if (!Array.isArray(palette) || palette.length === 0 || count <= 0) return []
  if (count === 1) return [palette[0]]
  if (palette.length <= count) return palette.slice(0, count)
  const maxIndex = palette.length - 1
  const used = new Set<number>()
  const picks: string[] = []
  for (let i = 0; i < count; i += 1) {
    let idx = Math.round((maxIndex * i) / (count - 1))
    if (used.has(idx)) {
      let offset = 1
      while ((idx - offset >= 0 || idx + offset <= maxIndex) && used.has(idx)) {
        const left = idx - offset
        const right = idx + offset
        if (left >= 0 && !used.has(left)) {
          idx = left
          break
        }
        if (right <= maxIndex && !used.has(right)) {
          idx = right
          break
        }
        offset += 1
      }
    }
    used.add(idx)
    picks.push(palette[idx])
  }
  return picks
}

export const chartJsPdfReadyPlugin = {
  id: 'chartJsPdfReady',
  afterRender: (chart: any) => {
    try {
      const canvas: HTMLCanvasElement | undefined = chart?.canvas
      if (!canvas) return
      canvas.dataset.chartLibrary = 'chartjs'
      canvas.dataset.chartReady = 'true'
    } catch {
      // noop
    }
  },
}

/** WCAG relative luminance (0 = black, 1 = white) for a parsed RGB color. */
function relativeLuminance(rgb: Rgb): number {
  const channel = (v: number): number => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
}

/**
 * Decide the theme-aware text colors a chart should draw with, given the color it INHERITS
 * from its container (`getComputedStyle(canvas).color`).
 *
 * Charts hardcode dark slide tokens (`--slide-text-primary`) for axis labels, which are only
 * readable on the always-light slide surface. In the app they render on the chat/report
 * surface, which is dark in dark mode → black-on-black labels. We fix this WITHOUT touching
 * slides by keying off the surface's own inherited text color:
 *   • dark surface (light inherited text, luminance > 0.5) → return the light color to use;
 *   • light surface / slides (dark inherited text) → return null (no-op → byte-identical render).
 */
export function themeAwareChartText(inheritedColor: string): { base: string; muted: string } | null {
  const rgb = parseCanvasColor((inheritedColor || '').trim())
  if (!rgb) return null
  if (relativeLuminance(rgb) <= 0.5) return null
  return { base: inheritedColor, muted: withCanvasAlpha(inheritedColor, 0.7) }
}

/**
 * Global plugin: on a dark surface, repaint a chart's axis labels / titles / legend in the
 * surface's inherited (light) text color so they stay readable in dark mode. On a light
 * surface it restores whatever it previously replaced, so toggling dark then light again
 * leaves the chart exactly as it was before the plugin ever touched it. It leaves light
 * mode and slide rendering untouched when nothing was ever patched (see `themeAwareChartText`),
 * and never blocks a draw.
 */
export const chartJsThemeTextPlugin = {
  id: 'chartJsThemeText',
  beforeDraw: (chart: any) => {
    try {
      if (typeof window === 'undefined') return
      const canvas: HTMLCanvasElement | undefined = chart?.canvas
      if (!canvas) return
      const inherited = getComputedStyle(canvas).color
      const colors = themeAwareChartText(inherited)
      const originals = chart.$themeTextOriginals

      if (!colors) {
        // Light surface: undo whatever this plugin previously patched, if anything.
        if (originals) {
          if ('color' in originals) chart.options.color = originals.color
          const legendLabels = chart?.legend?.options?.labels
          if (legendLabels && originals.legend && 'color' in originals.legend) {
            legendLabels.color = originals.legend.color
          }
          const scales = chart?.scales || {}
          for (const id of Object.keys(originals.scales || {})) {
            const opts = scales[id]?.options
            if (!opts) continue
            const scaleOriginal = originals.scales[id]
            if (opts.ticks && 'ticksColor' in scaleOriginal) opts.ticks.color = scaleOriginal.ticksColor
            if (opts.title && 'titleColor' in scaleOriginal) opts.title.color = scaleOriginal.titleColor
            if (opts.pointLabels && 'pointLabelsColor' in scaleOriginal) {
              opts.pointLabels.color = scaleOriginal.pointLabelsColor
            }
          }
          delete chart.$themeTextOriginals
        }
        return
      }

      const { base, muted } = colors
      // Remember the pre-patch values the first time we touch each field, so a later light
      // draw can restore exactly what was there before.
      const record = originals || { scales: {} }
      if (!('color' in record)) record.color = chart.options.color
      chart.options.color = base

      const legendLabels = chart?.legend?.options?.labels
      if (legendLabels) {
        record.legend = record.legend || {}
        if (!('color' in record.legend)) record.legend.color = legendLabels.color
        legendLabels.color = base
      }

      const scales = chart?.scales || {}
      for (const id of Object.keys(scales)) {
        const opts = scales[id]?.options
        if (!opts) continue
        record.scales[id] = record.scales[id] || {}
        const scaleRecord = record.scales[id]
        if (opts.ticks) {
          if (!('ticksColor' in scaleRecord)) scaleRecord.ticksColor = opts.ticks.color
          // Preserve the muted vs. primary distinction: ticks that pointed at the muted
          // slide token keep a softened tone; everything else uses the full text color.
          opts.ticks.color = String(opts.ticks.color ?? '').includes('muted') ? muted : base
        }
        if (opts.title) {
          if (!('titleColor' in scaleRecord)) scaleRecord.titleColor = opts.title.color
          opts.title.color = base
        }
        if (opts.pointLabels) {
          if (!('pointLabelsColor' in scaleRecord)) scaleRecord.pointLabelsColor = opts.pointLabels.color
          opts.pointLabels.color = base
        }
      }

      chart.$themeTextOriginals = record
    } catch {
      // noop — never block a chart from drawing
    }
  },
}
